import { Badge } from '@repo/ui/badge'
import { DataTable } from '@repo/ui/commons/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/commons/data-table/types'
import { postAdminUsersSearch } from '@repo/sdk/gen'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { UserRound } from 'lucide-react'
import { z } from 'zod'
import { BackofficeShell } from '@/components/backoffice-shell'
import { QueryErrorState } from '@/components/query-error-state'
import { useTranslation } from '@/lib/i18n'
import { requireBackofficeSession } from '@/lib/require-backoffice-session'
import { DebouncedSearchInput } from '../-components/debounced-search-input'
import { FilterSelect } from '../-components/filter-select'
import { ResetFiltersButton } from '../-components/reset-filters-button'
import { ToggleUserBanButton } from '../-components/toggle-user-ban-button'

const USER_STATUSES = ['all', 'active', 'banned', 'deleted'] as const
const EMAIL_VERIFICATION_STATES = ['all', 'verified', 'unverified'] as const
const MFA_STATES = ['all', 'enabled', 'disabled'] as const

export const Route = createFileRoute('/users/')({
	ssr: false,
	beforeLoad: requireBackofficeSession,
	validateSearch: z.object({
		direction: z.enum(['asc', 'desc']).optional(),
		emailVerified: z.enum(['all', 'verified', 'unverified']).optional(),
		mfa: z.enum(['all', 'enabled', 'disabled']).optional(),
		page: z.coerce.number().int().positive().optional(),
		q: z.string().optional(),
		status: z.enum(['all', 'active', 'banned', 'deleted']).optional(),
		sortBy: z.enum(['createdAt', 'name']).optional()
	}),
	component: UsersPage
})

function UsersPage() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	const navigate = useNavigate()
	const page = search.page ?? 1
	const sortBy = search.sortBy ?? 'createdAt'
	const direction = search.direction ?? 'desc'
	const usersQuery = useQuery({
		queryKey: ['backoffice', 'users', search],
		queryFn: () =>
			postAdminUsersSearch({
				body: {
					filters: {
						...(search.q ? { search: search.q } : {}),
						...(search.status && search.status !== 'all'
							? { status: [search.status] }
							: {}),
						...(search.emailVerified === 'verified'
							? { emailVerified: true }
							: {}),
						...(search.emailVerified === 'unverified'
							? { emailVerified: false }
							: {}),
						...(search.mfa === 'enabled'
							? { mfaEnabled: true }
							: {}),
						...(search.mfa === 'disabled'
							? { mfaEnabled: false }
							: {})
					},
					limit: 50,
					page,
					sort: { by: sortBy, direction }
				}
			}).then((response) => response.data)
	})
	const data = usersQuery.data?.data ?? []
	const hasActiveFilters = Boolean(
		search.q ||
			(search.emailVerified && search.emailVerified !== 'all') ||
			(search.mfa && search.mfa !== 'all') ||
			(search.status && search.status !== 'all')
	)
	const columns: DataTableColumn<(typeof data)[number], typeof sortBy>[] = [
		{
			id: 'name',
			header: t('common:user'),
			cell: (item) => (
				<Link
					className='flex items-center gap-3'
					params={{ id: item.id }}
					to='/users/$id'
				>
					<div className='grid size-9 place-items-center overflow-hidden rounded-full bg-primary/10 text-primary'>
						{item.image?.url ? (
							<img
								alt=''
								className='size-full object-cover'
								height={36}
								src={item.image.url}
								width={36}
							/>
						) : (
							<UserRound className='size-4' />
						)}
					</div>
					<div className='min-w-0'>
						<p className='truncate font-medium'>
							{item.name || t('common:unnamed_user')}
						</p>
						<p className='truncate text-muted-foreground text-xs'>
							{item.email ?? '—'}
						</p>
					</div>
				</Link>
			),
			sortKey: 'name',
			flex: 1
		},
		{
			id: 'status',
			header: t('common:status'),
			cell: (item) => (
				<Badge
					variant={
						item.status === 'banned' ? 'destructive' : 'outline'
					}
				>
					{item.status}
				</Badge>
			),
			width: 130
		},
		{
			id: 'security',
			header: t('common:user_security'),
			cell: (item) => (
				<div className='flex gap-1'>
					{item.mfaEnabled ? (
						<Badge variant='secondary'>MFA</Badge>
					) : null}
					{item.emailVerified ? (
						<Badge variant='secondary'>
							{t('common:verified')}
						</Badge>
					) : null}
				</div>
			),
			width: 170
		},
		{
			id: 'actions',
			header: t('common:actions'),
			cell: (item) => (
				<div className='flex justify-end gap-2'>
					<ToggleUserBanButton banned={item.banned} id={item.id} />
				</div>
			),
			pinned: 'right',
			width: 180,
			minWidth: 180,
			maxWidth: 180,
			enableResizing: false
		}
	]

	function updateSearch(next: Partial<typeof search>) {
		navigate({ to: '/users', search: { ...search, ...next } })
	}

	return (
		<BackofficeShell contentClassName='max-w-none'>
			<header>
				<p className='font-medium text-primary text-sm'>
					{t('common:backoffice')}
				</p>
				<h1 className='mt-2 font-semibold text-3xl tracking-tight'>
					{t('common:users')}
				</h1>
			</header>
			<div className='mt-6 flex flex-wrap gap-3'>
				<DebouncedSearchInput
					ariaLabel={t('common:search_users')}
					className='w-full sm:w-64'
					onValueChange={(q) =>
						updateSearch({ page: 1, q: q || undefined })
					}
					placeholder={t('common:search_users')}
					value={search.q ?? ''}
				/>
				<FilterSelect
					ariaLabel={t('common:filter_by_status')}
					items={[
						{ label: t('common:all_statuses'), value: 'all' },
						{ label: t('common:active'), value: 'active' },
						{ label: t('common:banned'), value: 'banned' },
						{ label: t('common:deleted'), value: 'deleted' }
					]}
					label={t('common:status')}
					onValueChange={(value) =>
						updateSearch({
							page: 1,
							status:
								value === 'all'
									? undefined
									: USER_STATUSES.find(
											(item) => item === value
										)
						})
					}
					value={search.status ?? 'all'}
				/>
				<FilterSelect
					ariaLabel={t('common:filter_by_email_verification')}
					items={[
						{
							label: t('common:all_verification_states'),
							value: 'all'
						},
						{ label: t('common:verified'), value: 'verified' },
						{ label: t('common:unverified'), value: 'unverified' }
					]}
					label={t('common:email_verification')}
					onValueChange={(value) =>
						updateSearch({
							emailVerified:
								value === 'all'
									? undefined
									: EMAIL_VERIFICATION_STATES.find(
											(item) => item === value
										),
							page: 1
						})
					}
					value={search.emailVerified ?? 'all'}
				/>
				<FilterSelect
					ariaLabel={t('common:filter_by_mfa')}
					items={[
						{ label: t('common:all_mfa_states'), value: 'all' },
						{ label: t('common:mfa_enabled'), value: 'enabled' },
						{ label: t('common:mfa_disabled'), value: 'disabled' }
					]}
					label={t('common:mfa')}
					onValueChange={(value) =>
						updateSearch({
							mfa:
								value === 'all'
									? undefined
									: MFA_STATES.find((item) => item === value),
							page: 1
						})
					}
					value={search.mfa ?? 'all'}
				/>
				<ResetFiltersButton
					disabled={!hasActiveFilters}
					onClick={() =>
						updateSearch({
							emailVerified: undefined,
							mfa: undefined,
							page: 1,
							q: undefined,
							status: undefined
						})
					}
				/>
			</div>
			<DataTable
				className='mt-6 h-[calc(100vh-18rem)] min-h-96'
				columns={columns}
				data={data}
				pagination={{
					onPageChange: (pageIndex) =>
						updateSearch({ page: pageIndex + 1 }),
					pageCount: usersQuery.data?.meta.totalPages ?? 0,
					pageIndex: page - 1
				}}
				sort={{
					onSortChange: (next) =>
						updateSearch({
							direction: next.direction,
							page: 1,
							sortBy: next.by ?? 'createdAt'
						}),
					state: { by: sortBy, direction }
				}}
				state={{
					empty: t('common:empty_users'),
					error: usersQuery.isError ? (
						<QueryErrorState
							message={t('common:users_load_error')}
							onRetry={() => usersQuery.refetch()}
						/>
					) : (
						false
					),
					loading: usersQuery.isPending
				}}
			/>
		</BackofficeShell>
	)
}
